import {
  Boxes,
  Camera,
  ChevronRight,
  Database,
  HardDrive,
  KeyRound,
  MemoryStick,
  Network,
  RefreshCw,
  Route,
  ShieldCheck,
  UserRound,
} from "lucide-react";
import { api } from "../api";
import {
  ActionButton,
  ErrorState,
  LoadingState,
  Panel,
  SandArcs,
  SectionHeader,
  StatusDot,
  Tag,
} from "../components/design-system";
import { useResource } from "../hooks/use-resource";
import type { RouteKey } from "../types";
import {
  formatBytes,
  formatDateTime,
  formatDuration,
  formatNumber,
} from "../utils";

export function OverviewPage({
  onNavigate,
}: {
  onNavigate: (route: RouteKey) => void;
}) {
  const resource = useResource(() => api.overview(), [], 30_000);
  const overview = resource.data;

  if (resource.loading && !overview) {
    return (
      <Panel>
        <LoadingState rows={8} />
      </Panel>
    );
  }

  if (!overview) {
    return resource.error ? (
      <ErrorState
        error={resource.error}
        onRetry={() => void resource.refresh()}
      />
    ) : null;
  }

  const allDependenciesUp = Object.values(overview.dependencies).every(
    (item) => item.status === "up",
  );
  const stats = [
    {
      label: "注册用户",
      value: formatNumber(overview.database?.users.total),
      hint: `近 24 小时活跃 ${formatNumber(overview.database?.users.active24h)} 位`,
      icon: UserRound,
      route: "users" as RouteKey,
    },
    {
      label: "有效会话",
      value:
        overview.activeSessions === null
          ? "—"
          : formatNumber(overview.activeSessions),
      hint: "Redis 中的滑动登录会话",
      icon: KeyRound,
      route: "users" as RouteKey,
    },
    {
      label: "持久快照",
      value: formatNumber(overview.database?.snapshots.total),
      hint: `${formatBytes(overview.database?.snapshots.payloadBytes)} 用户数据`,
      icon: Camera,
      route: "data" as RouteKey,
    },
    {
      label: "MinIO 对象",
      value: overview.storage
        ? formatNumber(overview.storage.objectCount)
        : "—",
      hint: `${formatBytes(overview.storage?.totalBytes)} 对象存储`,
      icon: Boxes,
      route: "data" as RouteKey,
    },
  ];

  const runtime = [
    ["后端版本", `v${overview.application.version}`],
    ["Node.js", overview.application.nodeVersion],
    ["运行时间", formatDuration(overview.application.uptimeSeconds)],
    ["进程内存", formatBytes(overview.application.memory.rssBytes)],
    ["Redis 键", overview.redis ? formatNumber(overview.redis.keys) : "—"],
    ["今日审计", formatNumber(overview.database?.audit.today)],
  ];

  return (
    <div className="relative space-y-10">
      <SandArcs className="absolute -top-10 right-0 z-0 w-[520px] max-w-full opacity-80" />

      <section className="relative z-[1]">
        <SectionHeader
          eyebrow="AT A GLANCE"
          title="今日概况"
          desc={`数据截至 ${formatDateTime(overview.generatedAt, true)} · 每 30 秒自动聚合`}
          className="rise-in"
          action={
            <ActionButton
              busy={resource.refreshing}
              onClick={() => void resource.refresh()}
            >
              <RefreshCw size={13} /> 刷新
            </ActionButton>
          }
        />
        {resource.error && (
          <div className="mt-4">
            <ErrorState
              error={resource.error}
              onRetry={() => void resource.refresh()}
            />
          </div>
        )}
        <div className="mt-5 grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
          {stats.map((stat, index) => {
            const StatIcon = stat.icon;
            return (
              <button
                key={stat.label}
                onClick={() => onNavigate(stat.route)}
                className={`rise-in rise-in-${index + 1} group relative overflow-hidden rounded-2xl border border-sand-light/80 bg-paper p-5 text-left shadow-[0_1px_2px_rgba(46,42,37,0.04)] transition-colors duration-300 hover:border-sand sm:p-6`}
              >
                <div className="relative z-[1] flex items-center justify-between">
                  <p className="text-[12.5px] font-medium text-ink-soft">
                    {stat.label}
                  </p>
                  <StatIcon
                    size={16}
                    strokeWidth={1.8}
                    className="text-ink-faint transition group-hover:text-clay"
                  />
                </div>
                <p className="relative z-[1] mt-4 font-serif-num text-[42px] leading-none tracking-tight text-ink sm:text-[48px]">
                  {stat.value}
                </p>
                <p className="relative z-[1] mt-3 text-[12px] leading-relaxed text-ink-faint">
                  {stat.hint}
                </p>
                <span className="absolute -bottom-8 -right-8 h-24 w-24 rounded-full bg-peach transition-transform duration-500 group-hover:scale-110" />
              </button>
            );
          })}
        </div>
      </section>

      <section className="grid gap-4 lg:grid-cols-5">
        <Panel className="rise-in rise-in-2 overflow-hidden lg:col-span-3">
          <div className="flex items-center justify-between border-b border-sand-light/70 px-6 py-4">
            <div>
              <p className="eyebrow text-clay">DEPENDENCIES</p>
              <h3 className="mt-1 font-display text-[16px] font-medium">
                基础设施
              </h3>
            </div>
            <Tag tone={allDependenciesUp ? "sage" : "danger"}>
              {allDependenciesUp ? "全部可用" : "存在异常"}
            </Tag>
          </div>
          <div className="divide-y divide-sand-light/60">
            {Object.values(overview.dependencies).map((dependency) => {
              const DependencyIcon =
                dependency.name === "mysql"
                  ? Database
                  : dependency.name === "redis"
                    ? MemoryStick
                    : HardDrive;
              return (
                <div
                  key={dependency.name}
                  className="group flex items-center gap-4 px-6 py-4 transition hover:bg-cream/60"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-peach text-clay-deep">
                    <DependencyIcon size={17} strokeWidth={1.8} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[14px] font-medium">
                      {dependencyName(dependency.name)}
                    </p>
                    <p className="mt-0.5 text-[12px] text-ink-faint">
                      服务连接与健康检查
                    </p>
                  </div>
                  <svg
                    width="56"
                    height="22"
                    viewBox="0 0 56 22"
                    className="hidden sm:block"
                    aria-hidden
                  >
                    <path
                      d="M0 16 L8 16 L11 8 L15 20 L19 4 L23 16 L32 16 L35 10 L39 18 L43 12 L47 16 L56 16"
                      stroke="#C15F3C"
                      strokeWidth="1.3"
                      fill="none"
                      opacity="0.6"
                    />
                  </svg>
                  <div className="text-right">
                    <p className="font-serif-num text-[16px] text-ink">
                      {dependency.latencyMs} ms
                    </p>
                    <p className="text-[11px] text-ink-faint">响应</p>
                  </div>
                  <StatusDot
                    tone={dependency.status === "up" ? "ok" : "down"}
                    label={dependency.status === "up" ? "正常" : "异常"}
                  />
                </div>
              );
            })}
          </div>
        </Panel>

        <Panel className="rise-in rise-in-3 lg:col-span-2">
          <div className="border-b border-sand-light/70 px-6 py-4">
            <p className="eyebrow text-clay">RUNTIME</p>
            <h3 className="mt-1 font-display text-[16px] font-medium">
              应用进程
            </h3>
          </div>
          <dl className="px-6 py-2">
            {runtime.map(([label, value]) => (
              <div
                key={label}
                className="flex items-baseline justify-between gap-4 border-b border-dashed border-sand-light/70 py-2.5 last:border-0"
              >
                <dt className="text-[12.5px] text-ink-soft">{label}</dt>
                <dd className="text-right text-[13.5px] font-medium text-ink">
                  {value}
                </dd>
              </div>
            ))}
          </dl>
        </Panel>
      </section>

      <section className="rise-in rise-in-4">
        <SectionHeader eyebrow="CAMPUS NETWORK" title="校园网络" />
        <Panel className="mt-5 overflow-hidden">
          <div className="grid divide-y divide-sand-light/60 md:grid-cols-3 md:divide-x md:divide-y-0">
            <VpnState
              icon={Network}
              title="Tailscale 主链路"
              status={vpnPrimaryLabel(overview.vpn.primary.status)}
              tone={overview.vpn.primary.status === "up" ? "sage" : "danger"}
              detail={
                overview.vpn.primary.campusReachable
                  ? `校内资源 ${overview.vpn.primary.campusLatencyMs ?? "—"} ms`
                  : overview.vpn.primary.health === "up"
                    ? "隧道在线，校内资源暂不可达"
                    : "容器或隧道离线"
              }
            />
            <VpnState
              icon={Route}
              title="当前请求链路"
              status={
                overview.vpn.activeMode === "tailscale"
                  ? "Tailscale"
                  : overview.vpn.activeMode === "legacy"
                    ? "备用 VPN"
                    : "不可用"
              }
              tone={overview.vpn.activeMode === "none" ? "danger" : "sage"}
              detail={`故障切换 ${formatNumber(overview.vpn.failover.count)} 次${
                overview.vpn.failover.lastAt
                  ? ` · 最近 ${formatDateTime(overview.vpn.failover.lastAt, true)}`
                  : ""
              }`}
            />
            <VpnState
              icon={ShieldCheck}
              title="aTrust 备用链路"
              status={
                overview.vpn.fallback.reconnecting
                  ? "正在重连"
                  : overview.vpn.fallback.connected
                    ? "已连接"
                    : "待命"
              }
              tone={overview.vpn.fallback.connected ? "sage" : "neutral"}
              detail={`主链路失败 ${overview.vpn.primary.attemptsBeforeFallback} 次后自动启用`}
            />
          </div>
          {overview.vpn.primary.lastError && (
            <div className="border-t border-sand-light/60 bg-cream/50 px-6 py-3 text-[12px] leading-relaxed text-ink-soft">
              最近异常：{overview.vpn.primary.lastError}
            </div>
          )}
        </Panel>
      </section>

      <section className="rise-in rise-in-4">
        <SectionHeader
          eyebrow="SCHEDULED JOBS"
          title="定时任务"
          action={
            <button
              onClick={() => onNavigate("jobs")}
              className="hidden items-center gap-1 text-[12.5px] text-ink-faint transition hover:text-clay-deep sm:flex"
            >
              查看全部 <ChevronRight size={14} />
            </button>
          }
        />
        <div className="mt-5 grid gap-4 md:grid-cols-2">
          {overview.jobs.slice(0, 2).map((job) => (
            <Panel
              key={job.name}
              className="group relative overflow-hidden p-6 transition hover:border-sand"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="eyebrow text-[10px] text-ink-faint">
                    {job.name}
                  </p>
                  <h4 className="mt-1.5 text-[15px] font-medium leading-snug">
                    {job.description}
                  </h4>
                </div>
                <Tag
                  tone={
                    job.status === "failed"
                      ? "danger"
                      : job.status === "success"
                        ? "sage"
                        : "neutral"
                  }
                >
                  {jobStatus(job.status)}
                </Tag>
              </div>
              <div className="mt-5 flex flex-wrap items-center gap-3 text-[12px] text-ink-soft">
                <code className="rounded-lg bg-cream-deep px-2.5 py-1 font-mono text-[11.5px] text-clay-deep">
                  {job.expression}
                </code>
                <span className="text-ink-faint">·</span>
                <span>{job.timezone}</span>
              </div>
              <span className="absolute -right-6 -top-6 h-20 w-20 rounded-full border border-sand opacity-0 transition-all duration-500 group-hover:opacity-100" />
            </Panel>
          ))}
          {!overview.jobs.length && (
            <Panel className="p-6 text-[13px] text-ink-faint">
              尚未注册定时任务。
            </Panel>
          )}
        </div>
      </section>
    </div>
  );
}

function VpnState({
  icon: Icon,
  title,
  status,
  tone,
  detail,
}: {
  icon: typeof Network;
  title: string;
  status: string;
  tone: "sage" | "danger" | "neutral";
  detail: string;
}) {
  return (
    <div className="flex min-w-0 items-start gap-4 px-6 py-5">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-peach text-clay-deep">
        <Icon size={17} strokeWidth={1.8} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-[14px] font-medium">{title}</h3>
          <Tag tone={tone}>{status}</Tag>
        </div>
        <p className="mt-1.5 text-[12px] leading-relaxed text-ink-faint">
          {detail}
        </p>
      </div>
    </div>
  );
}

function vpnPrimaryLabel(status: string) {
  return (
    (
      {
        up: "正常",
        down: "离线",
        degraded: "受限",
        unknown: "检查中",
      } as Record<string, string>
    )[status] || status
  );
}

function dependencyName(name: string) {
  return (
    (
      { mysql: "MySQL", redis: "Redis", minio: "MinIO" } as Record<
        string,
        string
      >
    )[name] || name
  );
}

function jobStatus(status: string) {
  return (
    (
      {
        idle: "等待运行",
        running: "运行中",
        success: "最近成功",
        failed: "最近失败",
      } as Record<string, string>
    )[status] || status
  );
}
